import { defineStore } from 'pinia';
import API from "@/shared/helpers/API";
import {useUserStore, useRoomsStore, useUnchangingStore} from "@/console-new/stores";
import {isAndroid, isNative} from "@/shared/helpers/webapp";
import {formatDateOnly, getLocalDateFromServer} from "@/shared/helpers/dates";

export const useRoomsOrdersStore = defineStore('roomsOrders', {
	state: () => ({
		shouldOpenFirstRoomWithNotification: false,
		loadingViewedCall: false,
		askedAssistanceFilter: false,
		lastPlayedCallSoundTime: null,
		minSecondsBetweenSounds: 30,
		roomsTablesCalls: [],
		manageReservationId: null,
		editReservationId: null,
		editReservationTables: false,
		newReservationTableId: null,
		reservations: [],
		reservationsTablesActive: false,
		reservationsSearch: null,
		reservationsSelectedDate: new Date(),
		reservationsCurrDate: [],
		reservationsFilters: {},
		reservationsDayToPrint: null,
		standardStatus: ["confirmed", "arrived"],
		allStatuses: ["confirmed", "arrived", "noshow", "canceled", "finished"]
	}),
	getters: {
		roomsStore: state => useRoomsStore(),
		roomsTablesCallsNotViewed: state => {
			if(!state.roomsTablesCalls) return [];
			return state.roomsTablesCalls.filter(call => !call.viewed);
		},
		rtcIds: state => {
			let finalIds = {};
			if(!state.roomsTablesCallsNotViewed) return finalIds;
			for(let i = 0; i < state.roomsTablesCallsNotViewed.length; i++) {
				finalIds[state.roomsTablesCallsNotViewed[i].room_table_call_id] = state.roomsTablesCallsNotViewed[i];
			}
			return finalIds;
		},
		rtcTablesIds: state => {
			let finalIds = {};
			if(!state.roomsTablesCallsNotViewed) return finalIds;
			for(let i = 0; i < state.roomsTablesCallsNotViewed.length; i++) {
				let currentItem = state.roomsTablesCallsNotViewed[i];
				if(!finalIds[currentItem.room_table_id]) {
					finalIds[currentItem.room_table_id] = {};
				}
				finalIds[currentItem.room_table_id][currentItem.room_table_call_id] = currentItem;
			}
			return finalIds;
		},
		rtcRoomsIds: state => {
			let finalIds = {};
			if(!state.roomsTablesCallsNotViewed) return finalIds;
			for(let i = 0; i < state.roomsTablesCallsNotViewed.length; i++) {
				let currentItem = state.roomsTablesCallsNotViewed[i];
				if(!finalIds[currentItem.room_id]) {
					finalIds[currentItem.room_id] = {};
				}
				finalIds[currentItem.room_id][currentItem.room_table_call_id] = currentItem;
			}
			return finalIds;
		},
		hasReservations: state => {
			return state.currBusiness && (state.currBusiness.active_plan === "allinone" || state.currBusiness.active_plan === "enterprise");
		}
	},
	actions: {
		getDayReservations(callback = null) {
			const userStore = useUserStore();
			if(userStore.currBusinessId === null) {
				// Call callback with empty data to clear loading state
				callback && callback([]);
				return;
			}

			const date = this.reservationsSelectedDate ? formatDateOnly(this.reservationsSelectedDate) : null;
			let status = date && date < formatDateOnly(new Date()) ? ["confirmed", "arrived", "noshow", "canceled", "finished"] : ["confirmed", "arrived"];

			if(this.reservationsFilters && Object.keys(this.reservationsFilters).length) {
				if(this.reservationsFilters.status && this.reservationsFilters.status.length) {
					status = this.reservationsFilters.status;
				}
			}
			let experiences = null;
			if(this.reservationsFilters && this.reservationsFilters.experiences && this.reservationsFilters.experiences.length) {
				experiences = this.reservationsFilters.experiences;
			}

			API.init().getDayReservations(userStore.currBusinessId, date, status, experiences, this.reservationsSearch, response => {
				if (response && response.result === "OK") {
					this.reservationsCurrDate = this.normalizeReservations(response.reservations);
					callback && callback(this.reservations);
				} else {
					this.reservationsCurrDate = [];
					callback && callback([]);
				}
			});
		},
		updateReservationStatus(reservationId, status, refund, callback = null) {
			const userStore = useUserStore();
			if(userStore.currBusinessId === null) return;

			API.init().updateReservationStatus(userStore.currBusinessId, reservationId, status, refund, response => {
				if (response && response.result === "OK") {
					callback && callback(response);
				} else {
					callback && callback();
				}
			});
		},
		normalizeReservations(reservations) {

			reservations.forEach(reservation => {
				reservation.fullDate = getLocalDateFromServer(reservation.date+" "+reservation.time);
				reservation.date = formatDateOnly(reservation.fullDate);
				reservation.time = reservation.fullDate.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'});
				reservation.timeDisplay = reservation.fullDate.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'});
				reservation.timeUntil = null;
				reservation.dateDisplay = reservation.fullDate.toLocaleDateString([], {month: 'short', day: '2-digit'});
				reservation.children = Number(reservation.children);
				reservation.dogs = Number(reservation.dogs);
				reservation.amount_charged = Number(reservation.amount_charged);
				reservation.notes = reservation.notes ? reservation.notes : null;
				reservation.loadingUpdate = false;

				reservation.tablesRoom = this.getTablesRoomFromIds(reservation.tables);

				reservation.allergensList = [];
				reservation.allergens.forEach(allergen => {
					const unchangingStore = useUnchangingStore();
					if(unchangingStore.allergens.hasOwnProperty(allergen)) {
						reservation.allergensList.push(unchangingStore.allergens[allergen]);
					}
				});
			});
			return reservations;
		},
		getTablesRoomFromIds(roomTableIds) {
			let result = [];
			roomTableIds.forEach(roomTableId => {

				this.roomsStore.roomsTables.forEach(room => {
					room.tables.forEach(table => {
						if(table.room_table_id === Number(roomTableId)) {
							result.push({
								table_room_id: table.room_table_id,
								room_name: room.name,
								table_name: table.table_name
							});
						}
					});
				});

			});
			return result;
		},
		getReservationById(reservationId) {
			let reservation = this.reservations.find(reservation => reservation.reservation_id === reservationId);
			if(!reservation) {
				reservation = this.reservationsCurrDate.find(reservation => reservation.reservation_id === reservationId);
			}
			return reservation;
		},
		playNewRoomTableCallSound(roomId) {
			if(this.lastPlayedCallSoundTime) {
				if(new Date().getTime() - this.lastPlayedCallSoundTime < this.minSecondsBetweenSounds * 1000) {
					return;
				}
			}
			if(!isAndroid()) {
				const foundRoom = this.roomsStore.roomsIds[roomId];
				if(!foundRoom || !foundRoom.notification_sound) {
					return;
				}
				this.lastPlayedCallSoundTime = new Date().getTime();
				if(foundRoom.audioNewOrder) {
					foundRoom.audioNewOrder.play().catch(() => {
						console.warn("Audio play not allowed");
					});
				} else {
					foundRoom.audioNewOrder = (new Audio('https://img.qromo.io/audio/'+foundRoom.notification_sound+'.mp3'));
					foundRoom.audioNewOrder.play().catch(() => {
						console.warn("Audio play not allowed");
					});
				}
			}
		},
		checkNewCallsToPlaySound(receivedCalls) {
			if(this.lastPlayedCallSoundTime) {
				if(new Date().getTime() - this.lastPlayedCallSoundTime < this.minSecondsBetweenSounds * 1000) {
					return;
				}
			}
			let newCalls = receivedCalls || [];
			if(newCalls && newCalls.length) {
				for(let i = 0; i < newCalls.length; i++) {
					let roomTableCall = newCalls[i];
					if(roomTableCall.room_table_call_id && !roomTableCall.viewed && !this.rtcIds[roomTableCall.room_table_call_id]) {
						this.playNewRoomTableCallSound(roomTableCall.room_id);
						return;
					}

				}
			}
		},
		getFirstRoomIdWithNotification() {
			if(!this.roomsStore.rooms?.length || !this.roomsTablesCalls?.length) { return null; }
			return this.roomsTablesCalls.find(call => call.room_id && this.roomsStore.roomsIds[call.room_id])?.room_id;
		},
		viewedRoomTableCall(roomTableId, callback = null) {
			if(this.loadingViewedCall) { return; }
			this.loadingViewedCall = true;
			API.init().viewedRoomTableCall(useUserStore().currBusinessId, roomTableId, (r) => {
				let successUpdate = r && r.result && r.result === "OK";
				if(successUpdate) {
					this.roomsTablesCalls = this.roomsTablesCalls.filter(call => call.room_table_id !== roomTableId);
				}
				this.loadingViewedCall = false;
				callback && callback(successUpdate);
			});
		},
		enabledTableCalls() {
			return this.roomsStore.rooms?.length && this.roomsStore.rooms?.find && !!this.roomsStore.rooms.find(room => room.enabledCalls && room.acceptOrders);
		},
		resetRoomsTablesCalls() {
			this.roomsTablesCalls = [];
			this.askedAssistanceFilter = false;
		},
		normalizeRoomsTablesCalls(calls) {
			return calls.map(call => {
				call.room_table_call_id = Number(call.room_table_call_id);
				call.room_table_id = Number(call.room_table_id);
				call.room_id = Number(call.room_id);
				call.viewed = call.hasOwnProperty('viewed') ? call.viewed : false;
				return call;
			})
		},
		initRoomsTablesCalls(calls) {
			let newCalls = this.normalizeRoomsTablesCalls(calls || []);
			this.checkNewCallsToPlaySound(newCalls);
			this.roomsTablesCalls = newCalls;
		},
	}
});